import { useCallback, useEffect, useMemo } from "react";

import { Box, Flex, Grid, SimpleGrid } from "@mantine/core";
import { useForm } from "@mantine/form";
import { useMask } from '@react-input/mask';
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";

import { Button, Container, InputLabel, LayoutStandard, TextInput, YesNoRadioButton } from "components";
import { IRootState } from "config/redux";
import { AppConstants, AuthorityConstants, RouteConstants, ServiceConstants } from "constant";
import { UserService } from "services";
import { displayClasses } from "styles";
import { ClientNewRequest } from "types";
import { AddressUtils, AppUtils } from "utils";

const { MASK_SWISS_PHONE_NUMBER } = AppConstants;
const { ROUTE_CLIENTS } = RouteConstants;
const { REQUEST_USERS_CLIENTS_NEW_ID, REQUEST_PENDING } = ServiceConstants;

const ClientNew = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const createServiceObject = useSelector((state: IRootState) => state.service.object[REQUEST_USERS_CLIENTS_NEW_ID]);
  const phoneNumberRef = useMask({
    mask: MASK_SWISS_PHONE_NUMBER,
    replacement: { _: /\d/ },
  });

  useEffect(() => {
    AppUtils.resolveServiceObject(createServiceObject, REQUEST_USERS_CLIENTS_NEW_ID, (data) => {
      navigate(ROUTE_CLIENTS + '/' + data.user.id);
    });
  }, [createServiceObject, navigate])

  const form = useForm({
    mode: 'uncontrolled',
    initialValues: {
      firstName: '',
      lastName: '',
      countryCode: '+41',
      phoneNumber: '',
      email: '',
    } as ClientNewRequest ,
    validateInputOnChange: true,
    validate: {
      firstName: (value: string) => AppUtils.isEmpty(value) ? t('error.required') : null,
      lastName: (value: string) => AppUtils.isEmpty(value) ? t('error.required') : null,
      phoneNumber: (value: string) => AppUtils.isEmpty(value) ? t('error.required') : null,
      email: (value: string) => {
        if (AppUtils.isEmpty(value)) {
          return t('error.required');
        }

        return AppUtils.isEmailValid(value) ? null : t('login.error.email');
      },
    },
  });

  const createNewClient = useCallback(() => {
    UserService.userClientsNew(form.getTransformedValues());
  }, [form]);

  const postalInputProps = form.getInputProps("postal");
  const isLoading = useMemo(() => (createServiceObject && createServiceObject.status === REQUEST_PENDING) || !form.isDirty() || !form.isValid(), [form, createServiceObject])

  return (
    <LayoutStandard
    authority={AuthorityConstants.ROLE_CR_C}
    title={AppUtils.composeTabTitle(t('clients.new.title'))}
    >
      <Container>
        <Box mb={'md'}>
          <Flex justify={'space-between'} align={'center'} className={displayClasses.header}>
            <Flex align={'center'}>
              <TextInput
                key={form.key('firstName')}
                label={t('user.firstName.label')}
                {...form.getInputProps('firstName')}
                style={{
                  fontWeight: 'bold',
                  margin: 0,
                  marginRight: 8
                }}
                required={true}
              />
              <TextInput
                key={form.key('lastName')}
                label={t('user.lastName.label')}
                {...form.getInputProps('lastName')}
                style={{
                  fontWeight: 'bold',
                  margin: 0,
                  marginRight: 8
                }}
                required={true}
              />
            </Flex>
          </Flex>
          <Box p={'md'}>
            <SimpleGrid cols={4}>
              <Box>
                <InputLabel label={t('user.phone.label')} required={true} />
                <Flex justify={"space-between"}>
                  <TextInput
                    key={form.key('countryCode')}
                    {...form.getInputProps('countryCode')}
                    w={"30%"}
                    disabled={true}
                  />
                  <TextInput
                    key={form.key('phoneNumber')}
                    placeholder={'79 111 11 11'}
                    {...form.getInputProps('phoneNumber')}
                    w={"65%"}
                    ref={phoneNumberRef}
                  />
                </Flex>
              </Box>
              <TextInput
                label={t('user.email.label')}
                key={form.key('email')}
                {...form.getInputProps('email')}
                required={true}
              />
              <Grid>
                <Grid.Col span={4}>
                  <TextInput
                    label={t('user.address.postal.label')}
                    key={form.key('postal')}
                    {...postalInputProps}
                    maxLength={4}
                    onChange={(event) => {
                      postalInputProps.onChange(event);

                      const city = AddressUtils.getCityFromPostal(event.currentTarget.value);
                      if (city) {
                        form.setFieldValue("city", city as any, { forceUpdate: true });
                      }
                    }}
                  />
                </Grid.Col>
                <Grid.Col span={8}>
                  <TextInput
                    label={t('user.address.city.label')}
                    key={form.key('city')}
                    {...form.getInputProps('city')}
                  />
                </Grid.Col>
              </Grid>
              <Grid>
                <Grid.Col span={8}>
                  <TextInput
                    label={t('user.address.street.label')}
                    key={form.key('street')}
                    {...form.getInputProps('street')}
                  />
                </Grid.Col>
                <Grid.Col span={4}>
                  <TextInput
                    label={t('user.address.houseNumber.label')}
                    key={form.key('houseNumber')}
                    {...form.getInputProps('houseNumber')}
                    onBeforeInput={(event: any) => {
                      const cursorPos = event.currentTarget.selectionStart || 0;
                      if (cursorPos === 0) {
                        const inputValue = (event as any).data;
                        if (inputValue && !/^[0-9]/.test(inputValue)) {
                          event.preventDefault();
                        }
                      }
                    }}
                  />
                </Grid.Col>
              </Grid>
            </SimpleGrid>

            <SimpleGrid cols={4} mt={'md'}>
              <YesNoRadioButton
                label={t('user.hasCredit.label')}
                name={'hasCredit'}
                form={form}
                rounded={true}
              />
              <YesNoRadioButton
                label={t('user.hasCreditCard.label')}
                name={'hasCreditCard'}
                form={form}
                rounded={true}
              />
              <YesNoRadioButton
                label={t('user.hasLeasing.label')}
                name={'hasLeasing'}
                form={form}
                rounded={true}
              />
              <YesNoRadioButton
                label={t('user.planningCredit.label')}
                name={'planningCredit'}
                form={form}
                rounded={true}
              />
            </SimpleGrid>
          </Box>
        </Box>
        <Box>
          <Button onClick={createNewClient} disabled={isLoading} mx={'sm'}>{t('clients.new.action')}</Button>
        </Box>
      </Container>
    </LayoutStandard>
  )
}

export default ClientNew;
